import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { DollarSign, Info } from "lucide-react";
import { useModules } from "../../context/ModuleContext";
import ModuleReadOnlyNotice from "../../components/reseller/ModuleReadOnlyNotice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResellerPricing({ store, onUpdate }) {
  // eDisplayroom off: the page still shows the funeral home its current
  // margins, but nothing can be changed. The API rejects these fields too.
  const { canWrite } = useModules();
  const readOnly = !canWrite("edisplayroom");
  const [globalMultiplier, setGlobalMultiplier] = useState(store.global_price_multiplier || 1.0);
  const [categoryMultipliers, setCategoryMultipliers] = useState(
    store.category_multipliers?.reduce((acc, m) => ({ ...acc, [m.category_id]: m.multiplier }), {}) || {}
  );
  const [overrideEnabled, setOverrideEnabled] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Initialize override enabled state
    const enabled = {};
    store.category_multipliers?.forEach(m => {
      enabled[m.category_id] = true;
    });
    setOverrideEnabled(enabled);
  }, []);


  const handleSave = async () => {
    setSaving(true);
    try {
      const activeMultipliers = Object.entries(categoryMultipliers)
        .filter(([catId]) => overrideEnabled[catId])
        .map(([category_id, multiplier]) => ({ category_id, multiplier }));

      await axios.put(`${API}/stores/my`, {
        global_price_multiplier: globalMultiplier,
        category_multipliers: activeMultipliers
      });
      toast.success("Pricing updated successfully");
      onUpdate();
    } catch (error) {
      toast.error("Failed to update pricing");
    } finally {
      setSaving(false);
    }
  };

  const toggleCategoryOverride = (categoryId) => {
    setOverrideEnabled(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
    if (!categoryMultipliers[categoryId]) {
      setCategoryMultipliers(prev => ({
        ...prev,
        [categoryId]: globalMultiplier
      }));
    }
  };

  const formatPrice = (basePrice, multiplier) => {
    return (basePrice * multiplier).toFixed(2);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="reseller-pricing">
      <ModuleReadOnlyNotice module="edisplayroom" />

      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          Price Multipliers
        </h2>
        <p className="text-slate-500">Set your pricing margins globally</p>
      </div>

      {/* Global Multiplier */}
      <div className="card-elevated p-6 space-y-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">
              e-Displayroom Price Multiplier
            </h3>
            <p className="text-sm text-slate-500">
              This multiplier applies to all e-Displayroom products unless overridden by a category-specific multiplier.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Slider
              value={[globalMultiplier]}
              onValueChange={(value) => setGlobalMultiplier(value[0])}
              disabled={readOnly}
              min={0.5}
              max={3}
              step={0.1}
              className="flex-1"
              data-testid="global-multiplier-slider"
            />
            <div className="w-24">
              <Input
                type="number"
                value={globalMultiplier}
                onChange={(e) => setGlobalMultiplier(parseFloat(e.target.value) || 1)}
                disabled={readOnly}
                step={0.1}
                min={0.5}
                max={5}
                className="h-12 text-center font-mono"
                data-testid="global-multiplier-input"
              />
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl">
            <p className="text-sm text-slate-600">
              <span className="font-medium">Example:</span> A product with base price $100 will be displayed as{" "}
              <span className="font-bold text-indigo-600">${formatPrice(100, globalMultiplier)}</span>
            </p>
          </div>
        </div>
      </div>


      {/* Info Box */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-medium mb-1">How pricing works</p>
          <p>
            Your customers will see the base product price multiplied by your chosen multiplier.
            <span className="block mt-1 font-medium text-amber-900 underline decoration-amber-300">Note: Products with a fixed price set by the admin will bypass all multipliers.</span>
          </p>
        </div>
      </div>

      {!readOnly && (
      <Button
        onClick={handleSave}
        disabled={saving}
        className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 h-12"
        data-testid="save-pricing-btn"
      >
        {saving ? "Saving..." : "Save Pricing"}
      </Button>
      )}
    </div>
  );
}
